/**
 * Navbat tartibini ustuvorlik raqamlariga aylantirish.
 *
 * Server navbatni `ORDER BY dispatch_rank, priority, created_at` bilan beradi,
 * ya'ni KICHIK raqam oldinda turadi. Dispetcher esa qatorlarni
 * sudrab joylashtiradi — natijani raqamga o'girish shu yerda.
 *
 * Asosiy qoida: IMKON QADAR KAM O'ZGARTIRISH. Har bir o'zgargan
 * qator serverga alohida so'rov bo'lib ketadi va har biri auditga
 * yozuv qoldiradi, shuning uchun butun ro'yxatni qayta raqamlash
 * bitta qatorni ko'chirish uchun juda qimmat.
 */

/** Yangi qiymat kerak bo'lganda qo'yiladigan oraliq. */
const STEP = 10;

export interface Ranked {
  id: string;
  priority: number;
  /** Navbat guruhi ({@link dispatchRank}); raqamlash guruh ichida. */
  rank?: number;
}

/**
 * Navbat guruhi — task-service `dispatch_rank` (migratsiya 0011) bilan
 * BIR XIL qoida. Server navbatni avval shu guruh, keyin ustuvorlik
 * bo'yicha beradi (buyurtmachi, 2026-11-03: inventarizatsiya va
 * joylashtirish birinchi, keyin vozvrat, popolnenie oxirida), shuning
 * uchun guruhlar orasida sudrash tartibni o'zgartirmaydi.
 */
export function dispatchRank(task: { task_type: string; priority: number; source_process?: string }): number {
  if (task.task_type === "COUNT" || task.task_type === "PUTAWAY") return 0;
  if (task.priority < 10) return 1;
  if (task.source_process === "RETURN_SESSION_PLACEMENT") return 2;
  if (task.source_process === "REPLENISHMENT_LIFT" || task.source_process === "REPLENISHMENT") return 4;
  return 3;
}

/**
 * Ro'yxat tartibiga mos ustuvorliklarni hisoblaydi.
 *
 * Qaytadi: faqat O'ZGARGAN qatorlar (id → yangi ustuvorlik).
 *
 * Algoritm — ketma-ketlikni o'sib boruvchi qilish: qiymati oldingi
 * qatordan katta bo'lgan qator O'Z QIYMATIDA qoladi, kichik yoki
 * teng bo'lgani esa oldingisi bilan keyingi «erkin» qiymat orasiga
 * joylashtiriladi. Shu tufayli bitta qatorni ko'chirish odatda
 * bir-ikkita o'zgarish beradi.
 */
export function rankPriorities(order: Ranked[]): Map<string, number> {
  const changes = new Map<string, number>();
  if (order.length === 0) return changes;

  // Joriy qiymatlar ustida ishlaymiz: o'zgarish bo'lsa shu yerda
  // yangilanadi va keyingi qadam uni ko'radi.
  const current = order.map((row) => row.priority);

  for (let index = 1; index < order.length; index++) {
    // Yangi guruh boshi — oldingi guruh bilan solishtirilmaydi.
    if (order[index]!.rank !== order[index - 1]!.rank) continue;
    const previous = current[index - 1]!;
    if (current[index]! > previous) continue;

    // Keyingi «erkin» qiymat — oldinda turgan (shu guruhda) va
    // previous dan katta bo'lgan birinchi qiymat. Undan oldin joy
    // bo'lsa, qolgan qatorlarga tegmaymiz.
    let ceiling: number | undefined;
    for (let ahead = index + 1; ahead < order.length; ahead++) {
      if (order[ahead]!.rank !== order[index]!.rank) break;
      if (current[ahead]! > previous) {
        ceiling = current[ahead]!;
        break;
      }
    }

    const value =
      ceiling !== undefined && ceiling - previous > 1
        ? // O'rtasiga: qolgan qatorlar o'z qiymatida qoladi.
          previous + Math.floor((ceiling - previous) / 2)
        : // Joy yo'q — qadam bilan oshiramiz va keyingi qadamlar
          // kerak bo'lsa ularni ham suradi.
          previous + STEP;

    current[index] = value;
    changes.set(order[index]!.id, value);
  }

  return changes;
}

/** Massivda elementni `from` dan `to` ga ko'chiradi. */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const result = items.slice();
  const [moved] = result.splice(from, 1);
  result.splice(to, 0, moved!);
  return result;
}
